(function () {
  const ROOT_SELECTOR = '[data-sa-ai-preset]';
  const FILTER_PARAM_PREFIXES = ['filter.', 'filter.v.', 'filter.p.'];

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const cssEscape = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value || '').replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  const formatMoney = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2
    }).format(amount);
  };

  const getUrlPresetId = (stateParam) => new URLSearchParams(window.location.search).get(stateParam);

  const updateUrlState = (stateParam, presetId) => {
    if (!stateParam || !window.history || !window.URL) return;
    const url = new URL(window.location.href);
    url.searchParams.set(stateParam, presetId);
    const searchParams = url.searchParams.toString();
    window.history.pushState({ searchParams }, '', `${url.pathname}${searchParams ? `?${searchParams}` : ''}${url.hash}`);
  };

  const hasExplicitFilters = () => {
    const params = new URLSearchParams(window.location.search);
    return [...params.entries()].some(([key, value]) => value !== '' && FILTER_PARAM_PREFIXES.some((prefix) => key.startsWith(prefix)));
  };

  const buildBadges = (product, preset, handle) => {
    const badges = [];
    const recommendationLabel = preset.cardBadges && preset.cardBadges[handle];
    if (recommendationLabel) badges.push(`<span class="badge badge--recommendation">${escapeHtml(recommendationLabel)}</span>`);
    if (product.compareAtPrice && Number(product.compareAtPrice) > Number(product.price)) badges.push('<span class="badge">Sale</span>');
    if (Number(product.price) >= 99) badges.push('<span class="badge badge--shipping">Free shipping</span>');
    return badges.slice(0, 3).join('');
  };

  const buildTechnicalBadges = (handle) => {
    const canonicalBadges = [...document.querySelectorAll(
      `[data-sa-product-handle="${cssEscape(handle)}"] .sa-card-glance .sa-feature-badge`
    )];
    return canonicalBadges.slice(0, 6).map((badge) => badge.outerHTML).join('');
  };

  const buildCard = (manifest, preset, handle, index) => {
    const product = manifest.products && manifest.products[handle];
    if (!product) return '';
    const title = escapeHtml(product.title);
    const url = escapeHtml(product.url);
    const image = escapeHtml(product.image);
    const price = formatMoney(product.price);
    const compare = product.compareAtPrice && Number(product.compareAtPrice) > Number(product.price)
      ? `<s>${formatMoney(product.compareAtPrice)}</s>`
      : '';
    const action = product.available && product.variantId && product.hasOnlyDefaultVariant !== false
      ? `<form method="post" action="/cart/add" class="sa-ai-card__form">
          <input type="hidden" name="id" value="${escapeHtml(product.variantId)}">
          <input type="hidden" name="quantity" value="1">
          <button type="submit">Add to cart</button>
        </form>`
      : `<a class="sa-ai-card__button" href="${url}">${product.available ? 'Choose options' : 'View product'}</a>`;
    return `
      <article class="sa-ai-card sa-ai-card--popular-starting-point" data-sa-ai-card data-product-handle="${escapeHtml(handle)}">
        <a class="sa-ai-card__media" href="${url}" aria-label="${title}">
          <span class="sa-ai-card__badges" aria-label="Product promotions">${buildBadges(product, preset, handle)}</span>
          <img src="${image}" alt="${title}" loading="${index === 0 ? 'eager' : 'lazy'}" decoding="async">
        </a>
        <div class="sa-ai-card__body">
          <h3><a href="${url}">${title}</a></h3>
          <div class="sa-ai-card__tech-list sa-card-glance" aria-label="Product At a Glance highlights">${buildTechnicalBadges(handle)}</div>
          <p class="sa-ai-card__price">${price} ${compare}</p>
          <div class="sa-ai-card__actions">
            ${action}
            <a class="sa-ai-card__details" href="${url}">View details</a>
          </div>
        </div>
      </article>`;
  };

  const updateProductCounts = (visibleCount, presetLabel, filterOverride) => {
    if (filterOverride) return;
    const text = `${visibleCount} ${visibleCount === 1 ? 'product' : 'products'} for ${presetLabel.toLowerCase()}`;
    document.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count, .sa-filter-panel__header p').forEach((target) => {
      target.textContent = text;
    });
  };

  const gridFor = (handle) => document.querySelector(`[data-sa-product-grid][data-sa-collection-handle="${cssEscape(handle)}"]`);

  const syncFullRange = (root, manifest, preset) => {
    const handle = root.getAttribute('data-sa-collection-handle');
    const grid = gridFor(handle);
    const handles = new Set(preset.fullRangeHandles || preset.rankedProductHandles || []);
    if (!grid || !handles.size) return;

    const items = [...grid.querySelectorAll('[data-sa-product-handle]')];
    const matchingItems = items.filter((item) => handles.has(item.getAttribute('data-sa-product-handle')));
    const filterOverride = hasExplicitFilters() && matchingItems.length === 0;
    let visibleCount = 0;
    items.forEach((item) => {
      const visible = filterOverride || handles.has(item.getAttribute('data-sa-product-handle'));
      item.hidden = !visible;
      item.classList.toggle('sa-ai-preset-hidden', !visible);
      if (visible) visibleCount += 1;
    });
    updateProductCounts(visibleCount, preset.label, filterOverride);
  };

  const renderPreset = (root, manifest, preset, options) => {
    const tabs = root.querySelectorAll('[data-sa-preset-tab]');
    const cards = root.querySelector('[data-sa-preset-cards]');
    tabs.forEach((tab) => {
      const active = tab.getAttribute('data-sa-preset-tab') === preset.id;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });
    if (cards) cards.innerHTML = (preset.rankedProductHandles || []).slice(0, 5).map((handle, index) => buildCard(manifest, preset, handle, index)).join('');
    root.setAttribute('data-active-preset', preset.id);
    syncFullRange(root, manifest, preset);
    if (options && options.updateUrl) updateUrlState(manifest.stateParam || 'sa_preset', preset.id);
  };

  const initRoot = async (root) => {
    if (root.dataset.saMerchandisingReady === 'true') return;
    root.dataset.saMerchandisingReady = 'true';
    const manifestUrl = root.getAttribute('data-sa-manifest-url');
    const handle = root.getAttribute('data-sa-collection-handle');
    const tabs = root.querySelector('[data-sa-preset-tabs]');
    if (!manifestUrl || !handle || !tabs) return;

    try {
      const response = await fetch(manifestUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Manifest request failed: ${response.status}`);
      const manifest = await response.json();
      const presets = Array.isArray(manifest.presets) ? manifest.presets : [];
      if (!presets.length || !manifest.collection || manifest.collection.handle !== handle) throw new Error('Manifest collection mismatch.');

      tabs.innerHTML = presets.map((preset) => `<button class="sa-ai-preset__tab" type="button" role="tab" data-sa-preset-tab="${escapeHtml(preset.id)}" aria-selected="false"><span>${escapeHtml(preset.label)}</span></button>`).join('');
      const presetById = new Map(presets.map((preset) => [preset.id, preset]));
      const initialId = getUrlPresetId(manifest.stateParam || 'sa_preset');
      const initialPreset = presetById.get(initialId) || presetById.get(manifest.defaultPreset) || presets[0];

      tabs.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-sa-preset-tab]');
        const preset = tab && presetById.get(tab.getAttribute('data-sa-preset-tab'));
        if (preset) renderPreset(root, manifest, preset, { updateUrl: true });
      });
      tabs.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const allTabs = [...tabs.querySelectorAll('[data-sa-preset-tab]')];
        const current = Math.max(0, allTabs.indexOf(document.activeElement));
        let next = current;
        if (event.key === 'ArrowLeft') next = (current - 1 + allTabs.length) % allTabs.length;
        if (event.key === 'ArrowRight') next = (current + 1) % allTabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = allTabs.length - 1;
        event.preventDefault();
        allTabs[next].focus();
        allTabs[next].click();
      });
      window.addEventListener('popstate', () => {
        const requested = getUrlPresetId(manifest.stateParam || 'sa_preset');
        const preset = presetById.get(requested) || presetById.get(manifest.defaultPreset) || presets[0];
        renderPreset(root, manifest, preset, { updateUrl: false });
      });
      document.addEventListener('sa:collection-grid:rendered', () => {
        const current = presetById.get(root.getAttribute('data-active-preset')) || initialPreset;
        window.requestAnimationFrame(() => syncFullRange(root, manifest, current));
      });
      renderPreset(root, manifest, initialPreset, { updateUrl: false });
    } catch (error) {
      root.classList.add('sa-ai-preset--error');
    }
  };

  const init = () => document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
